import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton, TableSkeleton, ListSkeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReceivingStore } from '../store/receivingStore';
import { ReceivingItemTypeEnum } from '../types/receiving.types';
import type { PurchaseOrderDto, PurchaseOrderSuggestionDto } from '../types/receiving.types';
import {
  ArrowLeft,
  Search,
  AlertCircle,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ClipboardList,
} from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function itemTypeBadge(type: ReceivingItemTypeEnum) {
  if (type === ReceivingItemTypeEnum.LotNumbered)
    return <Badge variant="secondary">Lot</Badge>;
  if (type === ReceivingItemTypeEnum.Serialized)
    return <Badge variant="outline">Serial</Badge>;
  return null;
}

export function PurchaseOrderSelectPage() {
  usePageTitle('Select Purchase Order');
  const navigate = useNavigate();
  const {
    pagedPOs,
    poTotal,
    poPage,
    poPageSize,
    isLoadingPOs,
    poError,
    suggestions,
    isLoadingSuggestions,
    selectedPreviewPO,
    isLoadingPreview,
    fetchPagedPurchaseOrders,
    searchSuggestions,
    clearSuggestions,
    fetchPreviewPOById,
    setPreviewPO,
    selectPurchaseOrder,
  } = useReceivingStore();

  const [searchInput, setSearchInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.ceil(poTotal / poPageSize);

  // Load paged POs on mount (restores page/pageSize from store state)
  useEffect(() => {
    fetchPagedPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear preview on unmount
  useEffect(() => {
    return () => {
      setPreviewPO(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced suggestion search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchInput.trim()) {
      clearSuggestions();
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      searchSuggestions(searchInput);
      setShowSuggestions(true);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, searchSuggestions, clearSuggestions]);

  // Hide suggestions on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSuggestionSelect(suggestion: PurchaseOrderSuggestionDto) {
    setSearchInput('');
    clearSuggestions();
    setShowSuggestions(false);
    await fetchPreviewPOById(suggestion.netSuitePOId);
  }

  function handleCardClick(po: PurchaseOrderDto) {
    setPreviewPO(po);
  }

  function handleSelectPO() {
    if (!selectedPreviewPO) return;
    selectPurchaseOrder(selectedPreviewPO);
    navigate(`/mro/receiving/new/${selectedPreviewPO.netSuitePOId}`);
  }

  function handlePageChange(newPage: number) {
    fetchPagedPurchaseOrders(newPage, poPageSize);
  }

  function handlePageSizeChange(value: string) {
    fetchPagedPurchaseOrders(1, Number(value));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mro/receiving')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">Select Purchase Order</h1>
      </div>

      {/* Master/detail split layout */}
      <div className="flex flex-col md:flex-row gap-4 items-start">
        {/* LEFT PANEL — compact PO card list */}
        <div className="w-full md:w-2/5 lg:w-1/3 shrink-0">
          <Card>
            <CardHeader>
              <CardTitle>Open Purchase Orders</CardTitle>
              <CardDescription>
                Select a purchase order to view details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Suggestion search */}
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                {isLoadingSuggestions && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground z-10" />
                )}
                <Input
                  placeholder="Search PO number or vendor..."
                  className="pl-9 pr-9"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onFocus={() => {
                    if (suggestions.length > 0) setShowSuggestions(true);
                  }}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border bg-popover shadow-md">
                    {suggestions.map((s) => (
                      <button
                        key={s.netSuitePOId}
                        className="w-full text-left px-4 py-2.5 hover:bg-muted/70 transition-colors flex items-center justify-between gap-4 text-sm"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSuggestionSelect(s);
                        }}
                      >
                        <span className="font-medium font-mono">{s.poNumber}</span>
                        <span className="text-muted-foreground truncate">{s.vendorName}</span>
                      </button>
                    ))}
                  </div>
                )}
                {showSuggestions && !isLoadingSuggestions && searchInput.trim() && suggestions.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border bg-popover shadow-md px-4 py-3 text-sm text-muted-foreground">
                    No matching purchase orders
                  </div>
                )}
              </div>

              {poError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{poError}</AlertDescription>
                </Alert>
              )}

              {/* Top pagination */}
              {!isLoadingPOs && poTotal > 0 && (
                <div className="flex items-center justify-end gap-3 text-sm">
                  <span className="text-muted-foreground">
                    {((poPage - 1) * poPageSize) + 1}–{Math.min(poPage * poPageSize, poTotal)} of {poTotal}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={poPage <= 1}
                      onClick={() => handlePageChange(poPage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={poPage >= totalPages}
                      onClick={() => handlePageChange(poPage + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* PO list */}
              {isLoadingPOs ? (
                <ListSkeleton rows={poPageSize > 10 ? 10 : poPageSize} className="py-1" />
              ) : pagedPOs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
                  <ShoppingCart className="h-8 w-8" />
                  <p>No open purchase orders</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {pagedPOs.map((po) => {
                    const isActive = selectedPreviewPO?.netSuitePOId === po.netSuitePOId;
                    const totalRemaining = po.lines.reduce((sum, l) => sum + l.remainingQuantity, 0);
                    return (
                      <div
                        key={po.netSuitePOId}
                        className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                          isActive
                            ? 'border-capitol-red-600 bg-capitol-red-50 dark:bg-capitol-red-950/20'
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => handleCardClick(po)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className={`font-medium font-mono text-sm ${isActive ? 'text-highlight' : ''}`}>
                              {po.poNumber}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{po.vendorName}</p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                            {new Date(po.poDate).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {po.lines.length} line{po.lines.length !== 1 ? 's' : ''} · {totalRemaining.toFixed(2)} remaining
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Bottom pagination controls */}
              {!isLoadingPOs && poTotal > 0 && (
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Rows</span>
                    <Select value={String(poPageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="h-8 w-16">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={poPage <= 1}
                      onClick={() => handlePageChange(poPage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={poPage >= totalPages}
                      onClick={() => handlePageChange(poPage + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT PANEL — detail view */}
        <div className="w-full md:flex-1 min-w-0 md:sticky md:top-4 md:self-start">
          {isLoadingPreview ? (
            <Card>
              <CardHeader>
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </CardHeader>
              <CardContent>
                <TableSkeleton rows={6} cols={7} />
              </CardContent>
            </Card>
          ) : !selectedPreviewPO ? (
            <Card className="h-full">
              <CardContent className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
                <ClipboardList className="h-10 w-10" />
                <p className="text-sm">Select a purchase order to view details</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="font-mono">{selectedPreviewPO.poNumber}</CardTitle>
                    <CardDescription>{selectedPreviewPO.vendorName}</CardDescription>
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {new Date(selectedPreviewPO.poDate).toLocaleDateString()}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedPreviewPO.inProgressReceivingCount > 0 && (
                  <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
                    <span className="font-semibold whitespace-nowrap">⚠ In&nbsp;progress:</span>
                    <span>
                      This PO already has {selectedPreviewPO.inProgressReceivingCount} receiving document
                      {selectedPreviewPO.inProgressReceivingCount > 1 ? 's' : ''} not yet posted. Check before
                      creating another to avoid a duplicate.
                    </span>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="pb-2 text-left font-medium">Item</th>
                        <th className="pb-2 text-right font-medium">Ordered</th>
                        <th className="pb-2 text-right font-medium">Received</th>
                        <th className="pb-2 text-right font-medium">Remaining</th>
                        <th className="pb-2 text-right font-medium">In&nbsp;progress</th>
                        <th className="pb-2 text-right font-medium">Available</th>
                        <th className="pb-2 text-left font-medium pl-3">UOM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPreviewPO.lines.map((line, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-xs">{line.itemCode}</span>
                              {itemTypeBadge(line.itemType)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">{line.itemDescription}</div>
                          </td>
                          <td className="py-2 text-right tabular-nums">{line.poQuantity.toFixed(2)}</td>
                          <td className="py-2 text-right tabular-nums">{line.quantityReceived.toFixed(2)}</td>
                          <td className="py-2 text-right tabular-nums">{line.remainingQuantity.toFixed(2)}</td>
                          <td className="py-2 text-right tabular-nums text-warning">
                            {line.quantityForPutAway > 0 ? line.quantityForPutAway.toFixed(2) : '—'}
                          </td>
                          <td className="py-2 text-right tabular-nums font-medium">
                            {(line.remainingQuantity - line.quantityForPutAway).toFixed(2)}
                          </td>
                          <td className="py-2 pl-3 text-muted-foreground">{line.unitOfMeasure}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={handleSelectPO} className="bg-capitol-red-600 hover:bg-capitol-red-700 text-white">
                    Select this PO
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
